/* eslint-disable camelcase */
import Head from 'next/head'
import { GLOBAL_CONSTANT } from '../utils/constants'
import { useRouter } from 'next/router'
import { PageTypeEnum } from '@App/libs/interfaces'
import { buildJsonLdSchema } from './Metadata.JSONLDSchema'
import { JOTAMetadata } from '@App/libs/utils/constants/metadata'
import { MetadataProps } from '../interfaces/Metadata.interface'

/**
 * Resolve description
 * @param {object} yoast
 * @param {string} description
 * @param {string} title
 * @return {string}
 */
export function resolveDescription(
    yoast:
        | {
              title?: string
              description?: string
              keywords?: string
          }
        | undefined,
    description: string,
    title: string
): string {
    return (
        yoast?.description ||
        description ||
        `Acompanhe todas as notícias, análises e informações sobre ${title} com o ${JOTAMetadata.shortname}.`
    )
}

/**
 * getFinalURL
 * Description: Get the final URL from the WP API or the canonical URL (from page)
 * @param {string} url
 * @param {URL} urlCanonical
 * @return {string}
 */
export function getFinalCanonicalURL(url: string, urlCanonical: URL) {
    if (url) {
        return new URL(url, JOTAMetadata.url).href
    }

    let resolvePathName = urlCanonical.pathname

    if (resolvePathName.startsWith('/content/tag')) {
        resolvePathName = resolvePathName.replace('/content/tag', '/tudo-sobre')
    } else if (
        resolvePathName.startsWith('/content/categories/coberturas-especiais')
    ) {
        resolvePathName = resolvePathName.replace(
            '/content/categories/coberturas-especiais',
            '/coberturas-especiais'
        )
    } else if (resolvePathName.startsWith('/content/autor')) {
        resolvePathName = resolvePathName.replace('/content/autor', '/autor')
    } else if (resolvePathName.startsWith('/content/colunas')) {
        resolvePathName = resolvePathName.replace(
            '/content/colunas',
            '/opiniao-e-analise/colunas'
        )
    } else {
        const genericPrefixes = ['/content/pro', '/content/info']
        for (const prefix of genericPrefixes) {
            if (resolvePathName.startsWith(prefix)) {
                resolvePathName = resolvePathName.replace(prefix, '')
                break
            }
        }
    }
    const urls = resolvePathName + urlCanonical.search
    return new URL(urls, JOTAMetadata.url).href
}

const Metadata = ({
    title,
    description,
    image = JOTAMetadata.urlLogo,
    revisitAfter = '1 minute',
    authors,
    yoast,
    dates,
    keywords,
    breadcrumb,
    pageType,
    url,
    noIndex = false,
    pagination
}: MetadataProps) => {
    const router = useRouter()
    const { asPath, query } = router
    const modificationDate =
        dates?.modification_iso8601 ?? dates?.publication_iso8601
    let urlFromPage: URL

    if (query?.type === 'post' && typeof query.content_url === 'string') {
        urlFromPage = new URL(query.content_url, JOTAMetadata.url)
    } else {
        urlFromPage = new URL(asPath, JOTAMetadata.url)
    }
    urlFromPage.hash = ''
    urlFromPage.search = ''
    const canonicalURL = new URL(getFinalCanonicalURL(url || '', urlFromPage))

    const pageTitle = yoast?.title ?? title ?? GLOBAL_CONSTANT.SITE_NAME

    const authorNames = authors?.length
        ? authors.map((a) => a.name).join(', ')
        : JOTAMetadata.name

    const descriptionValue = resolveDescription(yoast, description, title)
    const renderJsonLd = (type: string) => {
        const schema = buildJsonLdSchema(type, {
            pageType,
            canonicalURL,
            authors,
            breadcrumb,
            title: pageTitle,
            description: descriptionValue,
            imageURL: image,
            dates,
            JOTAMetadata
        })

        if (!schema) return null

        return (
            <script
                type='application/ld+json'
                data-schema={type}
                dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
            />
        )
    }
    // ATENÇÃO: Essas tags são para produção, não devem ser alteradas porque impactam direntamente o Google Discovery.
    let robots =
        'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1'

    if (process.env.NEXT_PUBLIC_NODE_ENV === 'production' && noIndex) {
        robots =
            'noindex, nofollow, max-image-preview:large, max-snippet:-1, max-video-preview:-1'
    } else if (process.env.NEXT_PUBLIC_NODE_ENV !== 'production') {
        robots = 'noindex'
    }

    return (
        <Head>
            {/* META TAGS */}
            <meta charSet='utf-8' />
            <meta
                name='viewport'
                content='width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=0'
            />
            <meta httpEquiv='X-UA-Compatible' content='ie=edge' />
            <meta name='cf-2fa-verify' content='ffe111bdb98aa1e' />
            <link rel='dns-prefetch' href='//c.jota.info' />

            <meta name='robots' content={robots} />

            <title>{pageTitle}</title>
            {keywords && <meta name='keywords' content={keywords} />}

            <meta name='revisit-after' content={revisitAfter} />
            <meta name='publisher' content={JOTAMetadata.name} />
            <meta name='description' content={descriptionValue} />

            <meta name='author' content={authorNames} />

            {/* OG Tags */}
            <meta property='og:locale' content='pt_BR' />
            <meta property='og:type' content='article' />
            <meta property='og:title' content={pageTitle} />
            <meta property='og:description' content={descriptionValue} />
            <meta property='og:url' content={canonicalURL.href} />
            <meta property='og:site_name' content={JOTAMetadata.name} />
            <meta property='og:image' content={image} />
            <meta property='og:image:secure_url' content={image} />
            <meta property='og:image:width' content='1200' />
            <meta property='og:image:height' content='630' />
            <meta property='og:author' content={authorNames} />
            <meta property='og:updated_time' content={modificationDate} />
            <meta
                property='og:published_time'
                content={dates?.publication_iso8601}
            />

            {/* Article Tags */}
            <meta
                property='article:publisher'
                content='https://www.facebook.com/jotajustica/'
            />
            <meta
                property='article:published_time'
                content={dates?.publication_iso8601}
            />

            <meta property='article:modified_time' content={modificationDate} />
            <meta property='article:section' content='Artigos' />
            <meta property='article:author' content={authorNames} />

            {/* Twitter Tags */}
            <meta property='twitter:card' content='summary_large_image' />
            <meta property='twitter:title' content={pageTitle} />
            <meta property='twitter:description' content={descriptionValue} />
            <meta property='twitter:creator' content='@jotainfo' />
            <meta property='twitter:image' content={image} />

            <meta itemProp='image' content={image} />
            <meta property='fb:app_id' content='1309294866226576' />

            <meta
                name='generator'
                content={`${JOTAMetadata.name} - ${JOTAMetadata.url}`}
            />

            {/* Link Tags */}
            {pagination && pagination.page !== 1 ? (
                <link
                    rel='canonical'
                    href={`${canonicalURL.href}?page=${pagination?.page}`}
                />
            ) : (
                <link rel='canonical' href={`${canonicalURL.href}`} />
            )}

            <link rel='icon' href='/favicon.ico' sizes='any' />

            <link
                rel='alternate'
                type='application/rss+xml'
                title='Feed do JOTA Jornalismo'
                href='https://portal.jota.info/feed/jotaunico'
            />

            <link
                rel='alternate'
                type='application/rss+xml'
                title='Feed do JOTA'
                href={`${JOTAMetadata.url}/feed`}
            />

            {/* Pagination */}
            {pagination && pagination.page < pagination.totalPages && (
                <link
                    rel='next'
                    href={`${canonicalURL.href}?page=${pagination?.page + 1}`}
                ></link>
            )}

            {pagination && pagination.page > 1 && (
                <link
                    rel='prev'
                    href={`${canonicalURL.href}?page=${pagination?.page - 1}`}
                ></link>
            )}

            {/* JSON-LD */}

            {renderJsonLd('NewsMediaOrganization')}
            {pageType === PageTypeEnum.HOME && renderJsonLd('WebSite')}
            {pageType === PageTypeEnum.AUTHOR && renderJsonLd('ProfilePage')}
            {pageType === PageTypeEnum.CATEGORY &&
                renderJsonLd('CollectionPage')}
            {(pageType === PageTypeEnum.POST ||
                pageType === PageTypeEnum.POSTPRO) &&
                renderJsonLd('NewsArticle')}
            {pageType !== PageTypeEnum.HOME && renderJsonLd('BreadcrumbList')}
        </Head>
    )
}

export default Metadata
